import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity, Zap, Stethoscope, Timer, TrendingUp, Gauge, Megaphone, DollarSign,
  Dumbbell, CalendarDays, Sun, Cloud, CloudRain, CloudSnow, CloudLightning, CloudFog, ExternalLink, Share2,
} from "lucide-react";
import NotifyMeButton from "@/components/NotifyMeButton";

function useRelTime(iso) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 5000);
    return () => clearInterval(t);
  }, []);
  if (!iso) return null;
  const s = Math.max(5, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function useGameCountdown(iso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);
  if (!iso) return null;
  const diff = Math.max(0, new Date(iso).getTime() - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return d > 0 ? `${d}d ${h}h` : `${h}h ${m}m`;
}

function wxMeta(code) {
  if (code == null) return null;
  if (code === 0) return [Sun, "Clear"];
  if (code <= 3) return [Cloud, "Partly Cloudy"];
  if (code <= 48) return [CloudFog, "Fog"];
  if (code <= 67) return [CloudRain, "Rain"];
  if (code <= 77) return [CloudSnow, "Snow"];
  if (code <= 82) return [CloudRain, "Showers"];
  return [CloudLightning, "Storms"];
}

function Num({ v }) {
  return <span key={String(v)} className="tick-pop tabular-nums">{v}</span>;
}

function Delta({ d, invert = false, testid }) {
  if (!d) return null;
  const up = d > 0;
  const good = invert ? !up : up;
  return (
    <span
      data-testid={testid}
      className={`font-cond font-bold text-[10px] tabular-nums ${good ? "text-emerald-400" : "text-red-400"}`}
      title="Change vs earlier today"
    >
      {up ? "▲" : "▼"}{Math.abs(d)}
    </span>
  );
}

function Row({ icon: Icon, label, children, accent, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 py-[9px]" data-testid={testid}>
      <span className="inline-flex items-center gap-2 font-cond uppercase tracking-[0.18em] text-[10px] text-white/55 shrink-0">
        <Icon size={12} className={accent || "text-titans-blue"} /> {label}
      </span>
      <span className="inline-flex items-center gap-1.5 min-w-0">{children}</span>
    </div>
  );
}

const GLASS = {
  background: "rgba(7,18,33,0.94)",
  backdropFilter: "blur(9px)",
  WebkitBackdropFilter: "blur(9px)",
  boxShadow: "0 18px 40px -24px rgba(0,0,0,0.65)",
};

/**
 * Titan Pulse — the signature live dashboard widget. Bloomberg Terminal meets ESPN.
 * Auto-refreshes via usePulse (60s) in the parent.
 */
export default function TitanPulse({ pulse, nextGame, trending }) {
  const rel = useRelTime(pulse?.last_update);
  const countdown = useGameCountdown(nextGame?.date);
  if (!pulse) return null;

  const opp = nextGame
    ? (nextGame.away?.abbr === "TEN" ? nextGame.home?.abbr : nextGame.away?.abbr)
    : null;
  const show = (k) => !pulse.rows || pulse.rows[k] !== false;
  const tOv = pulse.trending_override;
  const f = tOv || trending || pulse.featured;
  const wx = pulse.weather;
  const Wx = wx ? wxMeta(wx.code) : null;
  const ps = pulse.practice_status;
  const d = pulse.deltas || {};

  const shareOnX = () => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const text = `Titan Pulse — ${pulse.updates_today} Titans updates today${pulse.camp_day ? ` · Camp Day ${pulse.camp_day}` : ""}${typeof pulse.confidence === "number" ? ` · Fan confidence ${pulse.confidence}%` : ""} #TitanUp #Titans`;
    window.open(
      `https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(`${base}/api/og/share/pulse`)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <div className="rounded-xl border border-white/10 px-4 sm:px-5 py-4" style={GLASS} data-testid="titan-pulse">
      {/* header */}
      <div className="flex items-center gap-2 pb-2 border-b border-white/10">
        <Activity size={14} className="text-titans-red" />
        <span className="font-display uppercase tracking-wide text-lg text-white leading-none">Titan Pulse</span>
        <span className="live-dot" style={{ transform: "scale(0.75)" }} />
        {rel && (
          <span className="ml-auto font-cond uppercase tracking-wider text-[9px] text-white/40" data-testid="titan-pulse-updated">
            Updated {rel}
          </span>
        )}
      </div>

      <div className="divide-y divide-white/[0.07]">
        {show("updates") && (
        <Row icon={Zap} label="Live Updates" accent="text-titans-red" testid="pulse-updates">
          <Delta d={d.updates_today} testid="pulse-updates-delta" />
          <span className="font-display text-xl text-white leading-none"><Num v={pulse.updates_today} /></span>
        </Row>
        )}

        {show("injuries") && (
        <Row icon={Stethoscope} label="Injury Watch" testid="pulse-injuries">
          <Delta d={d.injuries_week} invert testid="pulse-injuries-delta" />
          <span className="font-display text-xl text-white leading-none"><Num v={pulse.injuries_week} /></span>
        </Row>
        )}

        {show("next_game") && (
        <Row icon={Timer} label={opp ? `Next Game · ${opp}` : "Next Game"} testid="pulse-next-game">
          <span className="font-display text-xl text-white leading-none"><Num v={countdown || "TBD"} /></span>
        </Row>
        )}

        {show("camp_day") && pulse.camp_day && (
          <Row icon={CalendarDays} label="Camp Day" testid="pulse-camp-day">
            <span className="font-display text-xl text-white leading-none"><Num v={pulse.camp_day} /></span>
          </Row>
        )}

        {show("practice") && ps && (
          <Row icon={Dumbbell} label="Practice" testid="pulse-practice">
            <span
              className={`font-cond uppercase tracking-wider text-[10px] px-2 py-1 rounded-full leading-none ${
                ps === "Live"
                  ? "bg-titans-red/15 text-titans-red border border-titans-red/40"
                  : ps === "Finished"
                  ? "bg-white/[0.06] text-white/70 border border-white/10"
                  : "bg-titans-blue/15 text-titans-blue border border-titans-blue/30"
              }`}
            >
              {ps === "Live" && <span className="live-dot inline-block mr-1.5" style={{ transform: "scale(0.6)" }} />}
              {ps}
            </span>
          </Row>
        )}

        {show("weather") && Wx && wx?.temp_f != null && (
          <Row icon={Wx[0]} label="Practice Weather" testid="pulse-weather">
            <span className="font-cond uppercase tracking-wider text-[10px] text-white/55">{Wx[1]}</span>
            <span className="font-display text-xl text-white leading-none"><Num v={`${wx.temp_f}°`} /></span>
          </Row>
        )}

        {show("trending") && f && (tOv ? (
          <a href={tOv.url || "#"} target={tOv.url ? "_blank" : undefined} rel="noopener noreferrer" className="flex items-center justify-between gap-3 py-[9px] group" data-testid="pulse-trending">
            <span className="inline-flex items-center gap-2 font-cond uppercase tracking-[0.18em] text-[10px] text-white/55 shrink-0">
              <TrendingUp size={12} className="text-titans-blue" /> Trending
            </span>
            <span className="font-headline font-semibold text-[12px] text-white/90 leading-snug text-right line-clamp-1 group-hover:text-titans-blue transition-colors">
              {tOv.headline}
            </span>
          </a>
        ) : (
          <Link to={`/news/${f.slug || f.id}`} className="flex items-center justify-between gap-3 py-[9px] group" data-testid="pulse-trending">
            <span className="inline-flex items-center gap-2 font-cond uppercase tracking-[0.18em] text-[10px] text-white/55 shrink-0">
              <TrendingUp size={12} className="text-titans-blue" /> Trending
            </span>
            <span className="inline-flex items-center gap-1.5 min-w-0">
              {f.published && Date.now() - new Date(f.published).getTime() < 2 * 3600 * 1000 && (
                <span className="shrink-0 bg-titans-red text-white font-cond font-bold uppercase tracking-[0.14em] text-[8px] leading-none px-1.5 py-[3px] rounded-full" data-testid="pulse-trending-new">
                  New
                </span>
              )}
              <span className="font-headline font-semibold text-[12px] text-white/90 leading-snug text-right line-clamp-1 group-hover:text-titans-blue transition-colors">
                {f.headline}
              </span>
            </span>
          </Link>
        ))}

        {show("roster_move") && pulse.roster_move && (
          <a
            href={pulse.roster_move.link || "#"}
            target="_blank"
            rel="noopener noreferrer"
            className="block py-[9px] group"
            data-testid="pulse-roster-move"
          >
            <span className="inline-flex items-center gap-2 font-cond uppercase tracking-[0.18em] text-[10px] text-white/55">
              <Megaphone size={12} className="text-titans-blue" /> Latest Roster Move
              <ExternalLink size={9} className="text-white/30" />
            </span>
            <span className="block font-headline font-semibold text-[12px] text-white/90 leading-snug line-clamp-2 mt-1 group-hover:text-titans-blue transition-colors">
              {pulse.roster_move.text}
            </span>
          </a>
        )}

        {show("cap") && typeof pulse.cap_room === "number" && (
          <Row icon={DollarSign} label="Cap Space" testid="pulse-cap">
            <Delta d={pulse.cap_delta} testid="pulse-cap-delta" />
            <span className="font-display text-xl text-white leading-none"><Num v={`$${pulse.cap_room}M`} /></span>
          </Row>
        )}

        {show("confidence") && typeof pulse.confidence === "number" && (
          <Link to="/community?tab=confidence" className="block py-[9px] group" data-testid="pulse-confidence">
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-2 font-cond uppercase tracking-[0.18em] text-[10px] text-white/55">
                <Gauge size={12} className="text-titans-blue" /> Fan Confidence
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Delta d={d.confidence} testid="pulse-confidence-delta" />
                <span className="font-display text-xl text-white leading-none"><Num v={`${pulse.confidence}%`} /></span>
              </span>
            </div>
            <div className="mt-2 h-1 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full rounded-full bg-titans-blue transition-[width] duration-700" style={{ width: `${pulse.confidence}%` }} />
            </div>
            <div className="font-cond uppercase tracking-wider text-[8px] text-white/35 mt-1.5 group-hover:text-titans-blue transition-colors">Cast your vote →</div>
          </Link>
        )}
      </div>

      <div className="pt-2.5 border-t border-white/10 space-y-2" data-testid="titan-pulse-footer">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <NotifyMeButton variant="compact" />
          <button
            type="button"
            onClick={shareOnX}
            data-testid="titan-pulse-share"
            className="inline-flex items-center gap-1.5 font-cond uppercase tracking-wider text-[9px] text-titans-blue hover:text-white transition-colors"
          >
            <Share2 size={10} /> Share Today's Pulse
          </button>
        </div>
        <span className="block font-cond uppercase tracking-wider text-[8px] text-white/30">Auto-refreshes every 60s</span>
      </div>
    </div>
  );
}

